import styled from 'styled-components';
import { motion } from 'framer-motion';

export const CarouselWrapper = styled.section`
    position: relative;
    width: 100%;
    background: #fff;
    padding: 4rem 2rem;

    @media (max-width: 768px) {
        padding: 3rem 1.5rem;
    }

    @media (max-width: 480px) {
        padding: 2rem 1rem;
    }
`;

export const CarouselHeader = styled.div`
    padding: 0 2rem 3rem;
    text-align: center;
    max-width: 800px;
    margin: 0 auto;

    @media (max-width: 768px) {
        padding: 0 1rem 2rem;
    }
`;

export const CarouselSection = styled.div`
    position: relative;
    width: 100%;
    max-width: 1200px;
    aspect-ratio: 1.563/1;
    margin: 1rem auto;
    overflow: hidden;
    background: #fff;
    border-radius: 40px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

    @media (max-width: 1200px) {
        max-width: 85%;
    }

    @media (max-width: 1024px) {
        max-width: 90%;
        margin: 3rem auto;
    }

    @media (max-width: 768px) {
        max-width: 90%;
        margin: 2rem auto;
        border-radius: 1.25rem;
        box-shadow: 0 0 60px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 480px) {
        max-width: 95%;
        margin: 1.5rem auto;
    }
`;

export const VideoContainer = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    -webkit-user-select: none;
    touch-action: pan-y;

    &:active {
        cursor: grabbing;
    }
`;

export const Video = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    user-select: none;
    -webkit-user-select: none;
    background: #fff;
    pointer-events: none;
`;

export const ProjectLink = styled(motion.a)`
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    width: 3.5rem;
    height: 3.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(93, 112, 246, 0.95);
    backdrop-filter: blur(10px);
    color: white;
    text-decoration: none;
    border-radius: 50%;
    z-index: 10;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
        background: rgba(93, 112, 246, 1);
        box-shadow: 0 15px 40px rgba(93, 112, 246, 0.5);
        transform: translateY(-2px) scale(1.05);
    }

    &:active {
        transform: translateY(0) scale(0.98);
    }

    @media (max-width: 768px) {
        top: 1rem;
        right: 1rem;
        width: 3rem;
        height: 3rem;
    }

    @media (max-width: 480px) {
        width: 2.75rem;
        height: 2.75rem;
    }
`;

export const LinkIcon = styled.svg`
    width: 1.5rem;
    height: 1.5rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;

    @media (max-width: 768px) {
        width: 1.3rem;
        height: 1.3rem;
    }

    @media (max-width: 480px) {
        width: 1.2rem;
        height: 1.2rem;
    }
`;

export const NavigationDots = styled.div`
    position: absolute;
    bottom: 3rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 1rem;
    z-index: 10;
    padding: 1rem 2rem;
    background: rgba(255, 255, 255, 1);
    backdrop-filter: blur(10px);
    border-radius: 50px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

    @media (max-width: 768px) {
        bottom: 2rem;
        gap: 0.75rem;
        padding: 0.75rem 1.5rem;
    }

    @media (max-width: 480px) {
        bottom: 1.5rem;
        gap: 0.5rem;
        padding: 0.6rem 1.2rem;
    }
`;

export const Dot = styled(motion.button)`
    width: ${props => props.$active ? '2.5rem' : '0.6rem'};
    height: 0.6rem;
    border-radius: 1rem;
    border: none;
    background: ${props => props.$active ?
        'rgba(93, 112, 246, 0.95)' :
        'rgba(0, 0, 0, 0.3)'};
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 0;
    outline: none;
    position: relative;
    overflow: hidden;

    &:hover {
        background: ${props => props.$active ?
            'rgba(255, 255, 255, 1)' :
            'rgba(255, 255, 255, 0.5)'};
        transform: scale(1.1);
    }

    &:active {
        transform: scale(0.95);
    }

    @media (max-width: 768px) {
        width: ${props => props.$active ? '2rem' : '0.5rem'};
        height: 0.5rem;
    }
`;

export const ProjectTitle = styled(motion.div)`
    position: absolute;
    bottom: 6rem;
    left: 50%;
    transform: translateX(-50%);
    padding: 0.75rem 1.5rem;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(10px);
    border-radius: 50px;
    color: white;
    font-family: "Outfit", sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    z-index: 10;
    white-space: nowrap;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

    @media (max-width: 1024px) {
        font-size: 1.1rem;
        bottom: 5.5rem;
    }

    @media (max-width: 768px) {
        font-size: 1rem;
        bottom: 5rem;
        padding: 0.6rem 1.2rem;
    }

    @media (max-width: 480px) {
        font-size: 0.9rem;
        bottom: 4.5rem;
        padding: 0.5rem 1rem;
        max-width: calc(100% - 2rem);
        white-space: normal;
        text-align: center;
    }
`;

export const DragHint = styled(motion.div)`
    position: absolute;
    bottom: 10rem;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255, 255, 255, 0.7);
    font-family: "Outfit", sans-serif;
    font-size: 0.85rem;
    font-weight: 400;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    pointer-events: none;

    @media (max-width: 768px) {
        bottom: 9rem;
        font-size: 0.75rem;
    }

    @media (max-width: 480px) {
        bottom: 8rem;
        font-size: 0.7rem;
    }
`;

export const LoadingOverlay = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #ffffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
`;

export const LoadingSpinner = styled(motion.div)`
    width: 60px;
    height: 60px;
    border: 3px solid rgba(93, 112, 246, 0.2);
    border-top-color: rgba(93, 112, 246, 1);
    border-radius: 50%;

    @media (max-width: 768px) {
        width: 40px;
        height: 40px;
        border-width: 2px;
    }
`;

export const SwipeIndicator = styled(motion.div)`
    position: absolute;
    top: 0;
    ${props => props.$direction === 'left' ? 'right: 0;' : 'left: 0;'}
    width: 120px;
    height: 100%;
    pointer-events: none;
    z-index: 8;
    background: ${props => props.$direction === 'left'
        ? 'linear-gradient(to left, #fff, transparent)'
        : 'linear-gradient(to right, #fff, transparent)'
    };
    opacity: ${props => props.$progress || 0};
    transition: opacity 0.1s ease-out;

    @media (max-width: 768px) {
        width: 80px;
    }
`;
